import type { SalesforcePaymentsExpressConfig } from '../../types/config';
import { SfPaymentMethodComponent } from '../sfp-payment-method-component';
import type { BillingDetails, PaymentIntentRequest, ShippingDetails } from '../../types/paymentRequest';
import { ResponseCode } from '../../types/paymentResponse';
import type { PaymentMethodType, VendorAccount } from '../../types/paymentMethodSet';
import type { PaymentMethodElement } from '../../types/paymentMethodSet';
import './adyen-styles.css';
import { addClass, adyenAmount, defer, removeClass } from '../../util/utility';
import { ExpressButtonShape } from '../../types/theme';

const DEFAULT_RENDER_TIMEOUT = 3000; // Button render timeout of 3 seconds

export abstract class SfpAdyenExpressComponent extends SfPaymentMethodComponent {
  TYPE: PaymentMethodType;
  ELEMENT: PaymentMethodElement;

  config: SalesforcePaymentsExpressConfig;
  paymentExpressButtonComponent: any;
  paymentRequest: PaymentIntentRequest;
  checkout: any;
  key: any;
  environment: any;

  billingDetails: BillingDetails;
  shippingDetails: ShippingDetails;
  paymentResolve: any;

  onRender: (types: PaymentMethodType[]) => void;
  renderTimeout = DEFAULT_RENDER_TIMEOUT;
  paymentAuthorized: Promise<unknown>;

  async initialize(): Promise<boolean> {
    this.paymentAuthorized = new Promise((resolve) => {
      this.paymentResolve = resolve;
    });

    if (!this.paymentExpressButtonComponent) {
      this.checkout = await this.vendorObject({
        amount: {
          value: adyenAmount(this.paymentRequest.currency, this.paymentRequest.amount),
          currency: this.paymentRequest.currency.toUpperCase(),
        },
        clientKey: this.paymentMethod.vendorAccount.config.key,
        environment: this.paymentMethod.vendorAccount.isLive ? 'live' : 'test',
        paymentMethodsResponse: this.paymentMethod.vendorAccount.config.paymentMethods,
        onSubmit: async (data: any, _component: any): Promise<void> => {
          await this.paymentAuthorized;
          const paymentData = {
            paymentMethod: data.data.paymentMethod,
            returnUrl: '',
            origin: window.location.origin,
            shippingDetails: this.shippingDetails,
            billingDetails: this.billingDetails,
          };
          const paymentIntentResponse = await this.config.actions.createIntent(paymentData);
          if (paymentIntentResponse) {
            const responseData = {
              paymentData: {
                id: paymentIntentResponse.pspReference
                  ? paymentIntentResponse.pspReference
                  : paymentIntentResponse.guid,
                uuid: paymentIntentResponse.guid,
                paymentGatewayId: this.paymentMethod.vendorAccount.gatewayId,
                type: this.TYPE,
                isManualCapture: this.config.options.useManualCapture ? 'true' : 'false',
              },
              paymentToken: paymentIntentResponse.guid,
              shippingDetails: this.shippingDetails,
              billingDetails: this.billingDetails,
            };
            const approveEvent = new CustomEvent('sfp:paymentapprove', {
              bubbles: true,
              composed: true,
              detail: {
                responseCode: ResponseCode.SUCCESS,
                data: responseData,
              },
            });
            this.element.dispatchEvent(approveEvent);
          } else {
            const errorEvent = new CustomEvent('sfp:paymenterror', {
              bubbles: true,
              composed: true,
              detail: {
                error: 'Payment Failed',
              },
            });
            this.element.dispatchEvent(errorEvent);
          }
        },
      });

      const configuration = this.generateConfiguration(
        this.config,
        this.paymentRequest,
        this.paymentMethod.vendorAccount
      );
      this.paymentExpressButtonComponent = this.checkout.create(this.TYPE, configuration);

      // Mount the payment request button on the DOM
      const paymentExpressButtonElement = document.createElement('div');
      paymentExpressButtonElement.className = `sfpp-payment-method-adyen-express sfpp-payment-method-adyen-express__loading`;
      paymentExpressButtonElement.style.display = 'none';
      paymentExpressButtonElement.style.setProperty(
        '--sfpp-adyen-express-buttons-locale',
        "'" + this.paymentRequest.locale + "'"
      );
      if (this.config.theme?.expressButtons?.buttonShape === ExpressButtonShape.PILL) {
        addClass(paymentExpressButtonElement, 'sfpp-payment-method-adyen-express-pill');
      }
      if (this.config.theme?.expressButtons?.buttonHeight) {
        const buttonHeight =
          Math.max(40, Math.min(this.config.theme.expressButtons.buttonHeight, 55)).toString() + 'px';
        paymentExpressButtonElement.style.setProperty('--sfpp-express-buttons-height', buttonHeight);
      }
      this.paymentExpressButtonComponent
        .isAvailable()
        .then(() => {
          this.paymentExpressButtonComponent.mount(paymentExpressButtonElement);
          paymentExpressButtonElement.style.display = 'block';
          removeClass(paymentExpressButtonElement, 'sfpp-payment-method-adyen-express__loading');
          this.onRender([this.TYPE]);
        })
        .catch(() => {
          paymentExpressButtonElement.remove();
          this.element = null;
          this.onRender([]);
        });
      this.element = paymentExpressButtonElement;
    }

    const deferred = defer<PaymentMethodType[]>(this.renderTimeout, []);
    this.renderPromise = deferred.promise;
    this.onRender = deferred.resolve;

    return Promise.resolve(true);
  }

  generateConfiguration(
    _config: SalesforcePaymentsExpressConfig,
    _paymentRequest: PaymentIntentRequest,
    _vendorAccount: VendorAccount
  ): any {
    // Implement in subclass as desired
  }

  getPaymentMethodType(): PaymentMethodType {
    return this.TYPE;
  }

  getCSSName(): string {
    return 'adyen-express';
  }

  hasError(): boolean {
    return false;
  }

  destroy(): void {
    this.paymentExpressButtonComponent?.unmount();
    this.paymentExpressButtonComponent = null;

    super.destroy();
  }
}
